import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { confirmPasswordReset } from '@/api/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { SetPasswordSchema, type SetPasswordValues } from './LoginSchema';

/**
 * Token-backed password reset page, reached from the branded SES reset email
 * link at /auth/reset-password/:token. The user chooses a new password, which
 * the backend commits to Cognito (AdminSetUserPassword) after validating the
 * single-use token.
 */
export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const form = useForm<SetPasswordValues>({
    resolver: zodResolver(SetPasswordSchema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
    mode: 'onTouched',
  });
  const { isSubmitting } = form.formState;

  if (done) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Password reset</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              Your password has been reset. You can now sign in with your new password.
            </AlertDescription>
          </Alert>
          <Button className="w-full" onClick={() => navigate('/auth/login')}>
            Go to sign in
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choose a new password</CardTitle>
        <CardDescription>Enter a new password for your account.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              setError(null);
              if (!token) {
                setError('This reset link is missing its token. Request a new one.');
                return;
              }
              try {
                await confirmPasswordReset(token, values.newPassword);
                setDone(true);
              } catch (err) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const e = err as any;
                setError(
                  e?.response?.data?.detail ||
                    e?.message ||
                    'Password reset failed. The link may have expired — request a new one.',
                );
              }
            })}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      autoComplete="new-password"
                      autoFocus
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>
                    At least 12 characters with an upper- and lowercase letter, a
                    number and a symbol.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmNewPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      autoComplete="new-password"
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Resetting…' : 'Reset password'}
            </Button>
          </form>
        </Form>

        <div className="text-center text-sm">
          <Link to="/auth/login" className="text-primary hover:underline">
            Back to sign in
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
